/**
 * A frase da espera entrando caractere a caractere e, terminada a entrada,
 * BRILHANDO — o `TextEffect` do design-hub (motion-primitives) refeito no que
 * a `LoadingPage` de fato usa: segmentação por caractere e o preset `blur`.
 *
 * **Sem biblioteca de animação, e isso é decisão, não economia.** O protótipo
 * usa `motion`, que aqui roda em WAAPI — cada caractere vira uma `Animation`
 * nativa. Os valores dela foram MEDIDOS no protótipo deployado e transcritos
 * em `loading_page.css`, então o movimento é o mesmo; o que não vem junto é uma
 * dependência nova num kit que não tinha nenhuma e um laço de quadros vivo em
 * cada suíte que encoste nesta tela.
 *
 * **O colapso no fim da entrada não é enfeite, é requisito.** O `shimmer`
 * pinta o texto com `background-clip: text`, e o recorte NÃO alcança o texto de
 * filhos `inline-block` — que é o que todo caractere animado é. Com os spans no
 * ar o gradiente existe, o texto fica com `-webkit-text-fill-color:
 * transparent` e a frase simplesmente não aparece. Por isso, terminada a
 * entrada, os segmentos viram um nó de texto só e o brilho liga no parágrafo.
 *
 * O preço é a saída deixar de ser escalonada e passar a ser do bloco inteiro —
 * e é o mesmo preço do protótipo, pela mesma razão: com a frase toda em cena,
 * desfazê-la letra por letra não diz nada que o fade do bloco não diga.
 */
import { Fragment, useMemo, useState, type CSSProperties } from 'react';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';

/** O nome do keyframe da entrada — o gatilho do colapso. */
const CHAR_ANIMATION = 'hui-loading-char-in';

/**
 * Duração e passo da entrada, EM MILISSEGUNDOS — os mesmos valores que
 * `loading_page.css` declara para `hui-loading-char-in` e para o
 * `animation-delay`. Vivem nos dois lugares porque CSS não lê constante de TS;
 * mexer num sem o outro desalinha `phraseEntryMs` do movimento real.
 */
const CHAR_DURATION_MS = 300;
const CHAR_STEP_MS = 30;

/** Medida de linha da frase, em `ch` — a régua do design-hub. */
const LINE_MEASURE_CH = 32;

function graphemesOf(text: string): string[] {
  return [...GRAPHEMES.segment(text)].map(({ segment }) => segment);
}

/**
 * Quanto a frase leva para terminar de entrar: o último caractere começa em
 * `(n-1) * passo` e dura `CHAR_DURATION_MS`. Quem manda na rotação usa isto
 * para não puxar a saída antes de a frase ter chegado inteira — sem ele, uma
 * frase longa some ainda entrando e nunca chega a brilhar.
 */
export function phraseEntryMs(text: string): number {
  const count = graphemesOf(text).length;
  return count === 0 ? 0 : (count - 1) * CHAR_STEP_MS + CHAR_DURATION_MS;
}

/**
 * Segmentação por GRAFEMA, e não o `split('')` do design-hub: acentuação
 * composta (`e` + U+0301) e emoji viram um segmento só, em vez de dois spans
 * que animam separados e desmontam o glifo no meio da entrada.
 */
const GRAPHEMES = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

export interface LoadingPhraseProps {
  /** A frase. Trocá-la reinicia a entrada (e o colapso). */
  readonly children: string;
  /** `true` roda a SAÍDA — quem desmonta o nó depois é o dono do relógio. */
  readonly leaving: boolean;
}

export function LoadingPhrase({ children, leaving }: LoadingPhraseProps) {
  const characters = useMemo(() => graphemesOf(children), [children]);
  // `prefers-reduced-motion` vivo — quem liga a preferência no meio da espera
  // vê a frase inteira já na próxima troca.
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [entered, setEntered] = useState(0);
  // A new phrase enters from its first character (reset during render).
  const [enteringPhrase, setEnteringPhrase] = useState(children);
  if (enteringPhrase !== children) {
    setEnteringPhrase(children);
    setEntered(0);
  }
  const collapsed = reduced || entered >= characters.length;

  return (
    <p
      className={`hui-loading-phrase text-heading text-foreground font-sans whitespace-pre-wrap${collapsed ? ' shimmer' : ''}`}
      data-leaving={leaving ? 'true' : 'false'}
      style={{ maxWidth: `${String(LINE_MEASURE_CH)}ch` }}
      onAnimationEnd={(event) => {
        // Só a entrada dos caracteres conta: o `shimmer` é infinito (nunca
        // termina) e a saída do bloco chega depois do colapso.
        if (event.animationName === CHAR_ANIMATION) setEntered((done) => done + 1);
      }}
    >
      {collapsed ? (
        children
      ) : (
        /* `key` na frase, e não só nos caracteres: sem ela React REUSA o span
           de todo índice cuja letra não mudou entre uma frase e a seguinte, e
           span reusado não reinicia animação — parte da frase nova entraria já
           em cena. */
        <Fragment key={children}>
          {/* A frase inteira pra quem lê por leitor de tela — cada caractere é
              `aria-hidden`, e um deles por vez não é uma frase. Era um
              `aria-label` no `<p>` no original, e ARIA proíbe rótulo em
              elemento sem papel (o axe reprova como `aria-prohibited-attr`). */}
          <span className="sr-only">{children}</span>
          {characters.map((character, index) => (
            <span
              key={`${String(index)}-${character}`}
              aria-hidden="true"
              className="hui-loading-char"
              style={{ '--hui-char-index': String(index) } as CSSProperties}
            >
              {character}
            </span>
          ))}
        </Fragment>
      )}
    </p>
  );
}
